import * as CL from '@design-system/component-library';
import * as React from 'react';
import { DialogType, StatusColor } from '../../common/enums.js';
import { EmployeeOnboardingRequest } from '../../generated/api/models.js';
import { Grid } from '../Grid/Grid.js';
import { HoverTooltip } from '../HoverToolTip/HoverTooltip.js';
import { ListSearch, Table } from '../Table/index.js';
import { Status } from '../Status/Status.js';
import { companyMsg, emailAddressMsg, t } from '../../common/i18n/index.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  formatTimeStampToHHmm,
  formatTimeStampToYYYYMMDDHHmm,
  formatTimestampToDDMMYYYY,
} from '../../common/utils/dateUtils.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { highlightKeyword } from '../../common/utils/searchFieldUtils.js';
import { initiateEmployeeOnBoarding, showDialog } from '../../selfservice/actions/index.js';
import { onEnterOrSpaceKeyPress } from '../../common/utils/handlerUtils.js';
import { sortArray } from '../../common/utils/arrayUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useDispatch } from 'react-redux';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import type { CLTableColumn } from '../../common/types/componentLibrary.js';
import type { CommonError } from '../../common/types/errors.js';
import type { DefaultListSearchParams } from '../Table/index.js';
import type { DialogParams } from '../../common/types/dialog.js';
import type { EmployeeOnBoardingPayload } from '../../selfservice/actions/index.js';

import './OnboardingRequestList.scss';

export interface OnboardingRequestListProps {
  onboardingRequests: EmployeeOnboardingRequest[];
}

interface ExtendedOnboardingRequest extends EmployeeOnboardingRequest {
  newState: string;
  color: StatusColor;
  sentDate: string;
  sentTime: string;
  expires: string;
}

interface TableSort {
  columnId: string;
  order: string;
}

const getOnboardingRequestExtendedFields = (
  onboardingRequest: EmployeeOnboardingRequest
): { newState: string; color: StatusColor; expires: string; sentDate: string; sentTime: string } => {
  const sentDate = formatTimestampToDDMMYYYY(onboardingRequest.createdDate) || '';
  const expireDate = formatTimeStampToYYYYMMDDHHmm(onboardingRequest.expirationDate);
  const sentTime = formatTimeStampToHHmm(onboardingRequest.createdDate);
  const expiresText = t.GBFN('Until {}', expireDate);

  switch (onboardingRequest.status) {
    case EmployeeOnboardingRequest.StatusEnum.ACTIVE:
      return {
        color: StatusColor.GREEN,
        newState: t.SFRK('Active'),
        expires: expiresText,
        sentDate,
        sentTime,
      };
    case EmployeeOnboardingRequest.StatusEnum.EXPIRED:
      return {
        color: StatusColor.RED,
        newState: t.PJQL('Expired'),
        expires: '',
        sentDate,
        sentTime,
      };
    default:
      return {
        color: StatusColor.GREY,
        newState: t.G87R('Used'),
        expires: '',
        sentDate,
        sentTime,
      };
  }
};

const getStatusColumn = (
  onboardingRequest: ExtendedOnboardingRequest,
  onInitiateEmployeeOnBoardingResend: (
    payload: { recipientEmails: string[] },
    validationErrors?: CommonError[]
  ) => void,
  onShowDialog: (params: DialogParams) => void
) => {
  const handleResend = () => {
    onShowDialog({ type: DialogType.SENDING_ONBOARDING_REQUEST });
    onInitiateEmployeeOnBoardingResend(
      { recipientEmails: onboardingRequest.employeeEmail.split(/\r\n|\n|\r/) },
      undefined
    );
  };

  switch (onboardingRequest.status) {
    case EmployeeOnboardingRequest.StatusEnum.ACTIVE:
      return [
        <Status
          key={0}
          color={onboardingRequest.color}
          textAfterBadge={
            <div className="of-onboarding-request-list active-state">
              <div>{onboardingRequest.newState}</div>
              <div className={`of-onboarding-request-list disclaimer ${dsClass.TEXT_XS}`}>
                {onboardingRequest.expires}
              </div>
            </div>
          }
        />,
      ];
    case EmployeeOnboardingRequest.StatusEnum.EXPIRED:
      return [
        <Status
          key={0}
          color={onboardingRequest.color}
          textAfterBadge={
            <div className="of-onboarding-request-list expired-state">
              <span>{onboardingRequest.newState}</span>
              <HoverTooltip tooltipText={t.HVWH('Resend')}>
                <span
                  aria-label="resend"
                  className="ea-icon ea-icon--small ea-icon--resend"
                  onClick={handleResend}
                  onKeyPress={(event: React.KeyboardEvent) => onEnterOrSpaceKeyPress(event, handleResend)}
                  role="button"
                  tabIndex={0}
                />
              </HoverTooltip>
            </div>
          }
        />,
      ];
    default:
      return [
        <Status
          key={0}
          color={onboardingRequest.color}
          textAfterBadge={<span key={0}>{onboardingRequest.newState}</span>}
        />,
      ];
  }
};

export const getSentColumn = (onboardingRequest: ExtendedOnboardingRequest) => {
  return [
    <div key={0} className="of-onboarding-request-list active-state">
      <div>{onboardingRequest.sentDate}</div>
      <div className={`of-onboarding-request-list disclaimer ${dsClass.TEXT_XS}`}>{onboardingRequest.sentTime}</div>
    </div>,
  ];
};

const getSendLinkButton = (onShowDialog: (params: DialogParams) => void) => {
  return (
    <div className="of-onboarding-request-list__send-link-btn">
      <CL.Button color="light" size="l" onClick={() => onShowDialog({ type: DialogType.ONBOARD_EMPLOYEE })}>
        {t.ANG6('Send invite link')}
      </CL.Button>
    </div>
  );
};

const filterOnboardingRequestsByEmail = (
  onboardingRequests: EmployeeOnboardingRequest[],
  searchQuery = ''
): EmployeeOnboardingRequest[] => {
  return onboardingRequests.filter(request =>
    request.employeeEmail?.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );
};

const sortList = (onboardingRequests: ExtendedOnboardingRequest[], sort?: TableSort) => {
  return sort
    ? sortArray(onboardingRequests, sort.columnId, sort.order)
    : sortArray(onboardingRequests, 'createdDate', 'desc');
};

const getExtendedOnboardingRequests = (onboardingRequests: EmployeeOnboardingRequest[], sort?: TableSort) => {
  const itemList: ExtendedOnboardingRequest[] = sortList(
    onboardingRequests.map(item => ({
      ...item,
      ...getOnboardingRequestExtendedFields(item),
    })),
    sort
  );
  if (sort?.columnId !== 'status') {
    return itemList;
  }
  return sort?.order === 'asc'
    ? itemList?.sort((a, b) => (a.newState < b.newState ? -1 : 1))
    : itemList?.sort((a, b) => (a.newState > b.newState ? -1 : 1));
};

export const OnboardingRequestList = ({ onboardingRequests }: OnboardingRequestListProps) => {
  const dispatch = useDispatch();
  const onShowDialog = (params: DialogParams) => dispatch(showDialog(params));
  const { authenticatedUser } = useAuth();
  const onInitiateEmployeeOnBoardingResend = (payload: EmployeeOnBoardingPayload, validationErrors?: CommonError[]) =>
    dispatch(initiateEmployeeOnBoarding(payload, validationErrors, true));
  const searchParams = useSearchParams<DefaultListSearchParams>();
  const { sort, order, search } = searchParams;

  const filteredOnboardingRequests = filterOnboardingRequestsByEmail(onboardingRequests ?? [], search);

  const extendedList = getExtendedOnboardingRequests(
    filteredOnboardingRequests,
    sort && order ? { columnId: sort, order } : undefined
  );

  const columns: CLTableColumn[] = [
    {
      key: 'employeeEmail',
      label: t.ZVQ5(emailAddressMsg),
      sortable: true,
      width: '30%',
    },
    {
      key: 'createdDate',
      label: t.E9I4('Sent'),
      sortable: true,
    },
    {
      key: 'onboardedBy',
      label: t.RVSW('Sender'),
      sortable: true,
    },
    {
      key: 'status',
      label: t.ASQT('Status'),
      sortable: true,
    },
    {
      key: 'companyName',
      label: t.KJTS(companyMsg),
      sortable: true,
    },
  ];

  const rows = extendedList?.map(onboardingRequest => ({
    employeeEmail: (
      <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER} ${dsClass.WHITE_SPACE_NOWRAP}`}>
        <CL.Icon
          className={`${dsClass.DISPLAY_FLEX} ${dsClass.MARGIN_RIGHT_2}`}
          color="blue-600"
          icon="send"
          type="filled"
        />
        {highlightKeyword(onboardingRequest.employeeEmail ?? '-', search)}
      </div>
    ),
    createdDate: getSentColumn(onboardingRequest),
    onboardedBy: <div>{onboardingRequest.onboardedBy}</div>,
    status: <div>{getStatusColumn(onboardingRequest, onInitiateEmployeeOnBoardingResend, onShowDialog)}</div>,
    companyName: <div>{getCompanyName(authenticatedUser, onboardingRequest.accountMasterId)}</div>,
  }));

  return (
    <Grid className="of-onboarding-request-list">
      <h2 className={`${dsClass.H3} ${dsClass.MARGIN_TOP_7} ${dsClass.MARGIN_BOTTOM_6}`}>
        {t.IU7C('Invites to Employee OmaElisa')}
      </h2>
      <ListSearch />
      {getSendLinkButton(onShowDialog)}
      <Table tableType="bordered" columns={columns} noItemsText={t.QYQX('No invites')} rows={rows || []} />
    </Grid>
  );
};
